import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    CircularProgress,
    Button,
    IconButton,
    Alert,
    Avatar,
    Grid,
    Divider,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import { ArrowBack, Check, Close, InsertDriveFile } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useKycDetails, useKycUpdateStatus } from 'hooks/useKyc';
import { format } from 'date-fns';

const KycDetailReview = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const { data: kyc, isLoading, error } = useKycDetails(userId);

    // Mutations
    const updateOverallStatus = useKycUpdateStatus();

    // Rejection Dialog State
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !kyc) {
        return (
            <Box p={4}>
                <Alert severity="error">Failed to load KYC details. The user might not have submitted one.</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/kyc')} sx={{ mt: 2 }}>
                    Back to Board
                </Button>
            </Box>
        );
    }

    const handleRejectClick = () => {
        setRejectReason('');
        setRejectDialogOpen(true);
    };

    const confirmReject = () => {
        updateOverallStatus.mutate({ id: kyc._id, status: 'rejected', message: rejectReason });
        setRejectDialogOpen(false);
    };

    const handleApproveOverall = () => {
        updateOverallStatus.mutate({ id: kyc._id, status: 'approved' });
    };

    const isUpdating = updateOverallStatus.isPending;

    const isCustomer = kyc.role === 'user' || kyc.userType === 'user';
    const isRider = kyc.role === 'rider' || kyc.user?.role === 'rider';
    const onlyNIN = isCustomer || isRider;

    // Sub-components for repeatable review cards
    const SectionReviewCard = ({ title, dataObj, fields }) => {
        if (!dataObj) return null;

        const docUrl = dataObj.documentUrl || dataObj.identityDocument || dataObj.addressDocument || dataObj.passportUrl;

        return (
            <Card
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        {title}
                    </Typography>
                </Stack>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Stack spacing={1}>
                            {fields.map((field) => (
                                <Box
                                    key={field.label}
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {field.value || 'N/A'}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Typography variant="subtitle2" mb={1}>
                            Document Attached
                        </Typography>
                        {docUrl ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 160,
                                    bgcolor: '#f0f0f0',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    cursor: 'pointer'
                                }}
                                onClick={() => window.open(docUrl, '_blank')}
                            >
                                {docUrl.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                                    <img
                                        src={docUrl}
                                        alt={`${title} Doc`}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Stack alignItems="center">
                                        <InsertDriveFile sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />{' '}
                                        <Typography variant="caption">View Document</Typography>
                                    </Stack>
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 160,
                                    bgcolor: '#f8f8f8',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    No Document Uploaded
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Card>
        );
    };

    return (
        <Box p={{ xs: 2, md: 4 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                <IconButton onClick={() => navigate('/admin/kyc')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                    <ArrowBack />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                        Applicant Review: {kyc.user?.firstName} {kyc.user?.lastName}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" color="error" onClick={handleRejectClick} disabled={isUpdating}>
                        Reject Master KYC
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleApproveOverall}
                        disabled={isUpdating || kyc.status === 'approved'}
                    >
                        {kyc.status === 'approved' ? 'KYC Complete' : 'Final Approve KYC'}
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    {/* User Summary Card */}
                    <Card sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 20 }}>
                        <Stack alignItems="center" spacing={2} mb={3}>
                            <Avatar src={kyc.user?.profilePicture} sx={{ width: 100, height: 100 }} />
                            <Box textAlign="center">
                                <Typography variant="h6" fontWeight="bold">
                                    {kyc.user?.firstName} {kyc.user?.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {kyc.user?.email} • {kyc.user?.phone}
                                </Typography>
                                <Chip
                                    label={kyc.status?.toUpperCase()}
                                    color={kyc.status === 'approved' ? 'success' : kyc.status === 'rejected' ? 'error' : 'warning'}
                                    sx={{ mt: 1 }}
                                />
                            </Box>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" color="text.secondary">
                            Submission Date
                        </Typography>
                        <Typography variant="body2" mb={2}>
                            {format(new Date(kyc.createdAt), 'PPpp')}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                            Account Role
                        </Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {kyc.user?.role || kyc.userType}
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    {/* NIN - Render for all roles if exists (Customer strictly uses this) */}
                    {kyc.nin && kyc.nin.nin && (
                        <SectionReviewCard
                            title="National Identification Number (NIN)"
                            dataObj={kyc.nin}
                            fields={[{ label: 'National Identification Number', value: kyc.nin.nin }]}
                        />
                    )}

                    {/* ONLY RENDER FULL KYC IF NOT A CUSTOMER OR RIDER */}
                    {!onlyNIN && (
                        <>
                            {/* IDENTITY */}
                            {kyc.identity && (
                                <SectionReviewCard
                                    title="Identity Verification"
                                    dataObj={kyc.identity}
                                    fields={[
                                        { label: 'Document Type', value: kyc.identity.identityType },
                                        { label: 'Document Number', value: kyc.identity.identityNumber }
                                    ]}
                                />
                            )}

                            {/* ADDRESS */}
                            {kyc.address && (
                                <SectionReviewCard
                                    title="Proof of Address"
                                    dataObj={kyc.address}
                                    fields={[
                                        { label: 'Street', value: kyc.address.street },
                                        { label: 'City / State', value: `${kyc.address.city}, ${kyc.address.state}` }
                                    ]}
                                />
                            )}

                            {/* NEXT OF KIN */}
                            {kyc.nextOfKin && (
                                <SectionReviewCard
                                    title="Next of Kin Details"
                                    dataObj={kyc.nextOfKin}
                                    fields={[
                                        { label: 'Name', value: kyc.nextOfKin.name },
                                        { label: 'Relationship', value: kyc.nextOfKin.relationship || 'N/A' },
                                        { label: 'Phone', value: kyc.nextOfKin.phone },
                                        { label: 'Address', value: kyc.nextOfKin.address }
                                    ]}
                                />
                            )}

                            {/* GUARANTOR */}
                            {kyc.guarantor && (
                                <SectionReviewCard
                                    title="Guarantor Details"
                                    dataObj={kyc.guarantor}
                                    fields={[
                                        { label: 'Name', value: kyc.guarantor.name },
                                        { label: 'Phone', value: kyc.guarantor.phone },
                                        { label: 'Address', value: kyc.guarantor.address }
                                    ]}
                                />
                            )}
                        </>
                    )}
                </Grid>
            </Grid>

            {/* Rejection Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject Application</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Please provide a reason for rejecting this document/application. The applicant will see this reason and be prompted
                        to re-upload.
                    </Typography>
                    <TextField
                        margin="dense"
                        label="Decline Reason"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmReject} variant="contained" color="error" disabled={!rejectReason.trim() || isUpdating}>
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KycDetailReview;
